// Money for the Music Wallet. The API sends integer minor units (cents) and a currency code; this file turns them into text
// and back. Typed amounts are parsed from their text, not through floating point, so 0.29 is always 29.

export function currencyDigits(currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions().maximumFractionDigits;
  } catch (e) {
    return 2;
  }
}

// 84250 -> "$842.50"; signed adds + or - in front ("+$164.00", "-$300.00").
export function formatMoney(minor, currency = "USD", { signed = false } = {}) {
  const value = Number(minor) || 0;
  const digits = currencyDigits(currency);
  let text;
  try {
    text = new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Math.abs(value) / 10 ** digits);
  } catch (e) {
    text = `${currency} ${(Math.abs(value) / 10 ** digits).toFixed(digits)}`;
  }
  if (value < 0) return `-${text}`;
  return signed && value > 0 ? `+${text}` : text;
}

// 84250 -> "842.50" (for the amount box and for messages such as "Minimum withdrawal is 25.00").
export function minorToInput(minor, currency = "USD") {
  const digits = currencyDigits(currency);
  const n = Math.max(0, Math.trunc(Number(minor) || 0));
  const text = String(n).padStart(digits + 1, "0");
  return digits === 0 ? text : `${text.slice(0, -digits)}.${text.slice(-digits)}`;
}

// What the person may type in the amount box: digits and one decimal point, no more decimals than the currency has.
export function cleanAmountInput(value, currency = "USD") {
  const digits = currencyDigits(currency);
  let text = String(value).replace(/[^\d.]/g, "");
  const dot = text.indexOf(".");
  if (dot !== -1) {
    const whole = text.slice(0, dot);
    const frac = text.slice(dot + 1).replace(/\./g, "").slice(0, digits);
    text = digits === 0 ? whole : `${whole}.${frac}`;
  }
  return text.replace(/^0+(?=\d)/, "").slice(0, 14);
}

// "842.50" -> 84250. Returns null when the text is not a usable amount.
export function parseAmount(text, currency = "USD") {
  const digits = currencyDigits(currency);
  const value = String(text || "").trim();
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(value)) return null;
  const [whole = "0", frac = ""] = value.split(".");
  if (frac.length > digits) return null;
  const minor = Number(`${whole || "0"}${frac.padEnd(digits, "0")}`);
  return Number.isSafeInteger(minor) ? minor : null;
}

export const formatDate = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "";
};
// A plain calendar day such as "2026-11-15" (no time, no timezone): shown as that same day wherever the viewer is.
export const formatCalendarDay = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!m) return formatDate(value);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
};
export const formatDateTime = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
};

export function newIdempotencyKey() {
  const c = typeof window !== "undefined" ? window.crypto : null;
  if (c && typeof c.randomUUID === "function") return `w-${c.randomUUID()}`;
  return `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export const NEGATIVE_BALANCE_TEXT = "A refund was larger than your balance; future earnings will cover it";
export const NOTHING_TO_WITHDRAW_TEXT = "Nothing to withdraw yet";

// The sentence shown near a disabled Withdraw button (and as its tooltip). "" when nothing needs saying.
export function withdrawHint(wallet) {
  if (!wallet) return "";
  const w = wallet.withdrawal || {};
  if (wallet.available_minor < 0) return NEGATIVE_BALANCE_TEXT;
  if (w.block_reason === "ARTIST_SUSPENDED") return w.block_message || "";
  if (wallet.available_minor === 0) return NOTHING_TO_WITHDRAW_TEXT;
  if (w.block_reason === "PAYOUT_HOLD" && wallet.payout?.hold_until) {
    return `${w.block_message}. It ends ${formatDateTime(wallet.payout.hold_until)}.`;
  }
  if (w.block_reason) return w.block_message || "";
  if (w.daily_limit_reached) return "You can withdraw again tomorrow";
  return "";
}

// The check made while typing in the Withdraw box. "" when the amount is fine. `minor` is the parsed amount or null.
export function amountError(text, minor, wallet) {
  const cur = wallet.currency;
  if (!String(text).trim()) return "Enter an amount to withdraw.";
  if (minor === null) return "Enter a valid amount.";
  if (minor <= 0) return "Enter an amount greater than zero.";
  if (minor > Math.max(0, wallet.available_minor)) return "Amount can't be more than your available balance";
  if (minor < wallet.withdrawal.minimum_minor) return `Minimum withdrawal is ${minorToInput(wallet.withdrawal.minimum_minor, cur)}`;
  return "";
}
